import React, { FC } from "react"
import {
  FlyoutProps,
  LineSegment,
  LineSegmentProps,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryLabel,
  VictoryLabelProps,
  VictoryTooltip,
} from "victory"
import { getHourFromTime, getMinutesFromTime } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { Sleep } from "../../utils/types"
import ChartHoverCrosshair from "../atoms/ChartHoverCrosshair"
import {
  TREND_CHART,
  chartAxisLabelStyle,
  chartTickLabelStyle,
  chartTooltipText,
} from "./charts/chartTypography"

const Label: FC<VictoryLabelProps> = ({ text, ...rest }) => (
  <VictoryLabel text={(text as string).split("\n")[0]} {...rest} />
)

const Line: FC<LineSegmentProps> = ({ index, ...props }) => (
  <LineSegment
    {...props}
    style={{
      strokeWidth: index === 4 ? 2 : 1,
      stroke: colors.neutral.clay,
    }}
  />
)

function sleepTimeFromTick(time: number) {
  let hour = getHourFromTime(time)
  let minutes = getMinutesFromTime(time)
  let unit = time < 0 ? "pm" : "am"
  if (minutes === "60") {
    hour = hour === 12 ? 1 : hour + 1
    minutes = "00"
    unit = hour === 12 ? "pm" : unit
  }
  if (hour === 0) {
    hour = 12
    unit = "pm"
  }
  return `${hour}:${minutes}${unit}`
}

const ToolTip: FC<{ datum?: Sleep } & FlyoutProps> = ({ x, y, datum }) => {
  return (
    <g>
      <ChartHoverCrosshair
        x={x}
        chartHeight={TREND_CHART.height}
        paddingTop={TREND_CHART.padding.top}
        paddingBottom={TREND_CHART.padding.bottom}
      />
      <rect
        x={x}
        y={(y ?? 0) + 25}
        height={54}
        width={160}
        rx={4}
        ry={4}
        fill={colors.neutral.space}
      />
      <text
        x={(x ?? 0) + 12}
        y={(y ?? 0) + 46}
        strokeWidth={0}
        {...chartTooltipText}
      >
        {sleepTimeFromTick(datum?.bedtime ?? 0)} to{" "}
        {sleepTimeFromTick(datum?.waketime ?? 0)}
      </text>
      <text
        x={(x ?? 0) + 12}
        y={(y ?? 0) + 68}
        strokeWidth={0}
        {...chartTooltipText}
      >
        {datum?.date}
      </text>
    </g>
  )
}

const chartWrapStyle: React.CSSProperties = {
  marginTop: 16,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
}

const SleepChart: FC<{
  sleep: Sleep[]
}> = ({ sleep }) => (
  <div style={chartWrapStyle}>
    <VictoryChart
      height={TREND_CHART.height}
      width={TREND_CHART.width}
      domainPadding={{ x: 48 }}
      padding={TREND_CHART.padding}
      containerComponent={
        <VictoryContainer
          responsive
          style={{ width: "100%", height: "auto" }}
        />
      }
    >
      <VictoryAxis
        orientation="top"
        offsetY={36}
        tickComponent={<></>}
        tickLabelComponent={<Label />}
        style={{
          tickLabels: chartTickLabelStyle,
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="right"
        dependentAxis={true}
        tickComponent={<></>}
        tickLabelComponent={<></>}
        style={{
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="bottom"
        offsetY={44}
        style={{
          tickLabels: chartTickLabelStyle,
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="left"
        dependentAxis={true}
        gridComponent={<Line />}
        label="Time of Day"
        axisLabelComponent={<VictoryLabel angle={-90} />}
        tickValues={[-16, -12, -8, -4, 0, 4, 8]}
        tickFormat={(tick: number) => {
          switch (tick) {
            case 8:
              return "4pm"
            case 4:
              return "8pm"
            case 0:
              return "Midnight"
            case -4:
              return "4am"
            case -8:
              return "8am"
            case -12:
              return "Noon"
            case -16:
              return "4pm"
            default:
              return ""
          }
        }}
        style={{
          tickLabels: chartTickLabelStyle,
          axisLabel: chartAxisLabelStyle,
          grid: { stroke: colors.neutral.clay },
          axis: { stroke: colors.neutral.clay },
        }}
      />

      <VictoryBar
        data={sleep}
        cornerRadius={{ top: 8, bottom: 8 }}
        barWidth={16}
        style={{
          data: { fill: colors.trendingData.red.dark },
        }}
        labelComponent={<VictoryTooltip flyoutComponent={<ToolTip />} />}
      />
    </VictoryChart>
  </div>
)

export default SleepChart
